/**
 * @file Construit le signal « empreinte bancaire » transmis avec le brouillon de parcours.
 * @module packages.common.components.table-reservation.utils.paymentSignal
 *
 * Le journal des non finalisés du dashboard ne voit qu'un badge « Empreinte »
 * quand l'écran de carte a été affiché. Ce signal distingue l'abandon
 * silencieux (rien saisi, carte saisie mais jamais validée) de l'échec réel
 * (carte refusée, réservation rejetée après validation de la carte). Fonctions
 * pures : elles renvoient la même référence quand rien ne change, pour que le
 * sync fire-and-forget ne renvoie pas un payload identique.
 *
 * Les libellés de sous-état et d'étape technique sont ceux attendus par le
 * backend (constants/draftPaymentSignal).
 */

/** Sous-états de l'étape empreinte, dans l'ordre de progression */
export const PAYMENT_SIGNAL_STATES = Object.freeze({
  DISPLAYED: 'displayed',
  CARD_ENTERED: 'card_entered',
  ATTEMPTED: 'attempted',
  AUTHENTICATION_PENDING: 'authentication_pending',
  CARD_VALIDATED: 'card_validated',
});

/** Étapes techniques où une erreur peut survenir */
export const PAYMENT_ERROR_STAGES = Object.freeze({
  PREPARE: 'prepare',
  CARD_INPUT: 'card_input',
  CONFIRM: 'confirm',
  CREATE: 'create',
});

const STATE_ORDER = [
  PAYMENT_SIGNAL_STATES.DISPLAYED,
  PAYMENT_SIGNAL_STATES.CARD_ENTERED,
  PAYMENT_SIGNAL_STATES.ATTEMPTED,
  PAYMENT_SIGNAL_STATES.AUTHENTICATION_PENDING,
  PAYMENT_SIGNAL_STATES.CARD_VALIDATED,
];

/** Longueur maximale du message libre conservé (même borne que le backend) */
const MAX_MESSAGE_LENGTH = 200;

const EMPTY_SIGNAL = Object.freeze({state: null, attempts: 0, lastError: null});

/**
 * Fait progresser le sous-état sans jamais le faire régresser.
 *
 * @param {Object|null} signal - Signal courant (ou null)
 * @param {string} state - Sous-état atteint (PAYMENT_SIGNAL_STATES)
 * @return {Object} Nouveau signal, ou la même référence si rien ne change
 */
export function advancePaymentState(signal, state) {
  const current = signal || EMPTY_SIGNAL;
  if (STATE_ORDER.indexOf(state) <= STATE_ORDER.indexOf(current.state)) return signal;
  return {...current, state};
}

/**
 * Enregistre une validation finale avec carte : sous-état `attempted` et
 * compteur incrémenté.
 *
 * @param {Object|null} signal - Signal courant (ou null)
 * @return {Object} Nouveau signal
 */
export function markPaymentAttempt(signal) {
  const current = signal || EMPTY_SIGNAL;
  return {...advancePaymentState(current, PAYMENT_SIGNAL_STATES.ATTEMPTED), attempts: (current.attempts || 0) + 1};
}

/**
 * Extrait un code stable d'une erreur, quelle que soit sa provenance.
 *
 * Priorité au code Stripe (stable, indépendant de la langue du navigateur),
 * puis au code métier renvoyé par l'API Brydge, puis au statut HTTP.
 *
 * @param {Object} error - Erreur Stripe.js, Axios ou native
 * @return {{code: string, declineCode: (string|null)}} Codes normalisés
 */
function extractErrorCodes(error) {
  if (!error) return {code: 'unknown', declineCode: null};

  const stripeCode = error.stripeCode || (error.type && error.code);
  if (stripeCode) return {code: stripeCode, declineCode: error.declineCode || error.decline_code || null};

  const response = error.response;
  if (response) {
    const apiCode = response.data?.code || response.data?.reason;
    return {code: apiCode || `http_${response.status}`, declineCode: null};
  }
  if (error.request) return {code: 'network_error', declineCode: null};

  return {code: error.code || 'unknown', declineCode: null};
}

/**
 * Enregistre une erreur sur le signal.
 *
 * @param {Object|null} signal - Signal courant (ou null)
 * @param {string} stage - Étape technique (PAYMENT_ERROR_STAGES)
 * @param {Object} error - Erreur d'origine
 * @return {Object} Nouveau signal portant `lastError`
 */
export function withPaymentError(signal, stage, error) {
  const current = signal || EMPTY_SIGNAL;
  const {code, declineCode} = extractErrorCodes(error);
  return {
    ...current,
    lastError: {
      stage,
      code,
      declineCode,
      message: String(error?.message || '').slice(0, MAX_MESSAGE_LENGTH) || null,
      at: new Date().toISOString(),
    },
  };
}
